/**
 * The item detail (docs/ux/casefile.md): the category header, the machine
 * and human pair the room reads from the back, the machine's own "Why
 * flagged" and "Look at" sentences verbatim, then the page viewer beside the
 * decision controls (or the locked line). One frame: evidence, the machine's
 * call, the human's call and the decision.
 */

import { cn } from 'cn';
import { Suspense, lazy, useCallback, useMemo, useState } from 'react';
import type { CasefileDocument, CasefileManifest, DecisionInput, SortReviewItem } from '@/app/types';
import { Skeleton } from '@/components/ui/skeleton';
import { dateTime } from '@/lib/format';
import { CATEGORY_LABEL, confidenceText, describeHuman, describeMachine, isEscalationWorthy, itemDoc, pagesText, viewerPlan, type ViewerPlan } from './describe';
import { DecisionControls } from './DecisionControls';
import { DecisionMark } from './ReviewQueue';

const PageRangeViewer = lazy(() => import('./PageRangeViewer'));

export function MachineHumanPair({ item, manifest, size = 'lg', className }: { item: SortReviewItem; manifest: CasefileManifest; size?: 'lg' | 'sm'; className?: string }) {
  const machine = describeMachine(item, manifest);
  const human = describeHuman(item, manifest);
  const by = item.by ? `by ${item.byName ?? item.by}${item.at ? `, ${dateTime(item.at)}` : ''}` : null;
  return (
    <div data-slot="machine-human" className={cn('grid gap-6 md:grid-cols-2', className)}>
      <div className="flex flex-col gap-1">
        <span className="eyebrow text-beam-text-muted">Machine</span>
        <p className={cn('font-medium', size === 'lg' ? 'text-[16px] leading-snug xl:text-[20px]' : 'text-[13px]')}>{machine.sentence}</p>
        {machine.sub ? <p className="text-[13px] text-beam-text-muted">{machine.sub}</p> : null}
      </div>
      <div className="flex flex-col gap-1">
        <span className="eyebrow text-beam-text-muted">Human</span>
        <p className={cn('font-medium', size === 'lg' ? 'text-[16px] leading-snug xl:text-[20px]' : 'text-[13px]')}>
          {item.decision === 'unresolved' ? <span className="text-beam-text-muted">{human.sentence}</span> : human.sentence}
        </p>
        <p className="text-[13px] text-beam-text-muted">{by ?? (item.category === 'additional' ? 'Choose a change below.' : 'Choose Accept, Change or Escalate below.')}</p>
        {item.decision !== 'unresolved' ? <span className="sr-only"><DecisionMark word={human.word} /></span> : null}
      </div>
    </div>
  );
}

export function WhyAndLookAt({ item, id }: { item: SortReviewItem; id: string }) {
  if (item.category === 'additional') return <p id={id} className="text-[13px]">The machine did not flag this document. Confidence {confidenceText(item.confidence)}.</p>;
  return (
    <div id={id} className="flex flex-col gap-1 text-[13px]">
      <p><span className="font-medium">Why flagged: </span>{item.uncertainty}</p>
      <p><span className="font-medium">Look at: </span>{item.lookAt}</p>
    </div>
  );
}

export type ItemDecisionProps = {
  matterRef: string;
  runId: string;
  item: SortReviewItem | null;
  /** A document picked for a new additional correction, when no item is open. */
  pickedDocument: CasefileDocument | null;
  manifest: CasefileManifest;
  escalationConfidence: number;
  locked: { by: string; at: string } | null;
  offline: boolean;
  busy: boolean;
  saveFailed: boolean;
  onDecide: (input: DecisionInput) => void;
  onWithdraw: () => void;
  onBack?: () => void;
  headingRef?: React.RefObject<HTMLHeadingElement | null>;
};

function pickedPlan(doc: CasefileDocument): ViewerPlan {
  return { mode: 'range', windows: [{ from: doc.startPage, to: Math.min(doc.endPage, doc.startPage + 11) }], extent: { from: doc.startPage, to: doc.endPage }, captions: {}, dashedBefore: [] };
}

export function ItemDecision(props: ItemDecisionProps) {
  const { matterRef, runId, item, pickedDocument, manifest, escalationConfidence, locked, offline, busy, saveFailed, onDecide, onWithdraw, onBack, headingRef } = props;
  const [enlarged, setEnlarged] = useState(() => { try { return window.sessionStorage.getItem('beam.casefile.enlarged') === '1'; } catch { return false; } });
  const [yourBoundary, setYourBoundary] = useState<number | null>(null);
  const onBoundaryPreview = useCallback((page: number | null) => setYourBoundary(page), []);
  const doc = item ? itemDoc(item, manifest) : pickedDocument ?? undefined;
  const plan = useMemo(() => (item ? viewerPlan(item, manifest) : pickedDocument ? pickedPlan(pickedDocument) : null), [item, pickedDocument, manifest]);
  const headingId = `item-heading`;
  const sentencesId = `item-sentences`;
  if (!plan) return null;

  const worthy = item ? isEscalationWorthy(item, escalationConfidence) : false;
  const heading = item ? CATEGORY_LABEL[item.category] : 'Additional correction';
  const range = item ? (item.category === 'low-confidence-boundary' ? pagesText(plan.extent.from, plan.extent.to) : pagesText(item.startPage, item.endPage)) : pickedDocument ? pagesText(pickedDocument.startPage, pickedDocument.endPage) : '';
  const saved = item && item.decision !== 'unresolved' && item.at ? { at: item.at, by: item.byName ?? item.by ?? '' } : null;

  const toggleEnlarge = () => setEnlarged((v) => { const next = !v; try { window.sessionStorage.setItem('beam.casefile.enlarged', next ? '1' : '0'); } catch { /* per-tab convenience only */ } return next; });

  return (
    <section role="region" aria-labelledby={headingId} data-slot="item-detail" className="flex flex-col gap-4 rounded-xl border border-beam-line bg-beam-paper p-4">
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 id={headingId} ref={headingRef} tabIndex={-1} className="eyebrow text-beam-text-muted outline-none focus-visible:ring-3 focus-visible:ring-ring/50">{heading}</h2>
        <span className="tabular text-[13px]">{range}</span>
        {item && item.category !== 'additional' ? <span className="tabular text-[13px] text-beam-text-muted">Confidence {confidenceText(item.confidence)}</span> : null}
        {worthy ? <span className="text-[13px] font-medium text-beam-warning">Escalation-worthy (under {escalationConfidence})</span> : null}
        {onBack ? <button type="button" className="ml-auto text-[13px] text-beam-signature underline-offset-4 hover:underline" onClick={onBack}>Back to summary</button> : null}
      </header>
      {item ? (
        <>
          <MachineHumanPair item={item} manifest={manifest} />
          <WhyAndLookAt item={item} id={sentencesId} />
        </>
      ) : pickedDocument ? (
        <div data-slot="machine-human" className="grid gap-6 md:grid-cols-2">
          <div className="flex flex-col gap-1"><span className="eyebrow text-beam-text-muted">Machine</span><p className="text-[16px] font-medium leading-snug xl:text-[20px]">Filed under {pickedDocument.section} as {pickedDocument.title}.</p></div>
          <div className="flex flex-col gap-1"><span className="eyebrow text-beam-text-muted">Human</span><p className="text-[16px] font-medium leading-snug text-beam-text-muted xl:text-[20px]">Not raised.</p><p className="text-[13px] text-beam-text-muted">Choose a correction below.</p></div>
          <p id={sentencesId} className="text-[13px] md:col-span-2">The machine did not flag this document. Confidence {confidenceText(pickedDocument.confidence)}.</p>
        </div>
      ) : null}
      <div className={cn('grid gap-6', !enlarged && 'xl:grid-cols-[minmax(0,1fr)_318px] 2xl:grid-cols-[minmax(0,1fr)_360px]')}>
        <Suspense fallback={<Skeleton className="h-[560px] rounded-xl" />}>
          <PageRangeViewer matterRef={matterRef} runId={runId} plan={plan} yourBoundary={yourBoundary} enlarged={enlarged} onEnlarge={toggleEnlarge} describedBy={sentencesId} offline={offline} />
        </Suspense>
        <DecisionControls
          item={item}
          document={doc}
          manifest={manifest}
          escalationConfidence={escalationConfidence}
          locked={locked}
          offline={offline}
          busy={busy}
          saveFailed={saveFailed}
          saved={saved}
          onSubmit={onDecide}
          onWithdraw={onWithdraw}
          onBoundaryPreview={item?.category === 'low-confidence-boundary' ? onBoundaryPreview : undefined}
        />
      </div>
    </section>
  );
}
